import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";
import { commitmentKeys, projectContractorKeys } from "@/lib/query";

interface Commitment {
  id: string;
  orgId: string | null;
  projectId: string | null;
  companyId: string | null;
  companyName: string | null;
  projectName: string | null;
  commitmentNumber: string | null;
  commitmentType: string | null;
  direction: string | null;
  title: string | null;
  description: string | null;
  status: string | null;
  originalAmount: string | null;
  approvedChangesAmount: string | null;
  revisedAmount: string | null;
  pendingChangesAmount?: string | null;
  totalBilledToDate: string | null;
  totalPaidToDate?: string | null;
  retentionPercent: string | null;
  scopeOfWork?: string | null;
  startDate: string | null;
  scheduledEndDate: string | null;
  executedDate?: string | null;
  toCompanyId?: string | null;
  fromCompanyId?: string | null;
  primaryBudgetLineId?: string | null;
  contractDocumentPath?: string | null;
}

export interface CommitmentLineItem {
  id: string;
  commitmentId: string;
  itemNo: string;
  description: string;
  scheduledValue: string;
  costCodeId: string | null;
  costCode?: string | null;
  costCodeDescription?: string | null;
}

export type CommitmentDetail = Commitment & {
  lineItems?: CommitmentLineItem[];
};

/**
 * Extended create-payload shape. Beyond the display fields in `Commitment`,
 * the create endpoint also accepts an optional G703 `lineItems` array and
 * a `primaryBudgetLineId` for linking back to a Buy Log row.
 */
export interface CreateCommitmentPayload extends Partial<Commitment> {
  lineItems?: Array<{
    itemNo: string;
    description: string;
    scheduledValue: string;
    costCodeId?: string | null;
  }>;
}

export interface UpdateCommitmentPayload extends Partial<Commitment> {
  id: string;
  lineItems?: Array<{
    itemNo: string;
    description: string;
    scheduledValue: string;
    costCodeId?: string | null;
    isAllowance?: boolean;
  }>;
}

export type { Commitment };

export function useCommitments(
  filters?: { projectId?: string; companyId?: string },
  options?: { enabled?: boolean },
) {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set("projectId", filters.projectId);
  if (filters?.companyId) params.set("companyId", filters.companyId);
  const qs = params.toString();

  return useQuery({
    queryKey: [...commitmentKeys.all, filters],
    queryFn: () => apiFetch<Commitment[]>(`/erp-commitments${qs ? `?${qs}` : ""}`),
    enabled: options?.enabled ?? true,
  });
}

export function useCommitment(id: string) {
  return useQuery({
    queryKey: commitmentKeys.detail(id),
    queryFn: () => apiFetch<CommitmentDetail>(`/erp-commitments/${id}`),
    enabled: !!id,
  });
}

export function useCreateCommitment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCommitmentPayload) =>
      apiPost<{ data: Commitment }>("/erp-commitments", data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: commitmentKeys.all });
      // Buy Log reads from the budget query, and createCommitment writes
      // linked_commitment_id + awarded_company_id back to the source line.
      // Invalidate the budget query for the affected project so the row
      // reflects the new commitment without a manual refresh.
      const projectId = res.data?.projectId;
      if (projectId) {
        qc.invalidateQueries({ queryKey: ["budget", projectId] });
        qc.invalidateQueries({ queryKey: projectContractorKeys.project(projectId) });
      }
      toast.success("Commitment created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateCommitment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: UpdateCommitmentPayload) =>
      apiPatch<{ data: Commitment }>(`/erp-commitments/${id}`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: commitmentKeys.all });
      qc.invalidateQueries({ queryKey: commitmentKeys.detail(variables.id) });
      toast.success("Commitment updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateCommitmentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiPatch<{ data: Commitment }>(`/erp-commitments/${id}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commitmentKeys.all });
      toast.success("Commitment status updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/**
 * Uploads a contract document PDF/doc to a commitment. Used by the Buy Log
 * Create Commitment flow as an optional second step after commitment creation.
 * The server stores the file in S3 and records the path on
 * `commitments.contract_document_path`.
 */
export function useUploadContractDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`/api/erp-commitments/${id}/contract-document`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `Upload failed: ${res.status}` }));
        throw new Error((err as { error?: string }).error ?? `Upload failed: ${res.status}`);
      }
      return res.json() as Promise<{ data: Commitment }>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commitmentKeys.all });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteCommitment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/erp-commitments/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commitmentKeys.all });
      toast.success("Commitment deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
